import { useCallback } from 'react';
import { useNavigate } from 'react-router';
import { Button, Field, Input } from '@mgdlead/ui';

import { AuthLayout } from '../components/AuthLayout';
import { FormAlert } from '@mgdlead/ui';
import { NativeSelect } from '@mgdlead/ui';
import { useSession } from '../auth/useSession';
import { useCreateWorkspace } from '../lib/api';
import { signOut } from '../lib/auth';
import {
  DEFAULT_LOCALE,
  LOCALE_OPTIONS,
  TIMEZONE_OPTIONS,
  guessTimezone,
} from '../lib/locale-options';
import { workspaceSchema } from '../lib/schemas';
import type { WorkspaceValues } from '../lib/schemas';
import { useDocumentTitle } from '../lib/useDocumentTitle';
import { useZodForm } from '../lib/useZodForm';

export function OnboardingPage() {
  useDocumentTitle('Criar workspace');
  const navigate = useNavigate();
  const { user } = useSession();
  const createWorkspace = useCreateWorkspace();

  const initial: WorkspaceValues = {
    name: '',
    // fuso do navegador quando for um dos oferecidos; senão, America/Sao_Paulo
    timezone: guessTimezone(),
    locale: DEFAULT_LOCALE,
  };

  const onSubmit = useCallback(
    async (values: { name: string; timezone: string; locale: string }) => {
      const workspace = await createWorkspace.mutateAsync(values);
      void navigate(`/w/${workspace.slug}`, { replace: true });
    },
    [createWorkspace, navigate],
  );

  const form = useZodForm({ schema: workspaceSchema, initialValues: initial, onSubmit });

  return (
    <AuthLayout
      title="Crie seu primeiro workspace"
      description={
        user
          ? `Boas-vindas, ${user.name}. Um workspace guarda seus grupos, campanhas e números.`
          : 'Um workspace guarda seus grupos, campanhas e números.'
      }
      footer={
        <button
          type="button"
          onClick={() => {
            void signOut().then(() => navigate('/login', { replace: true }));
          }}
          className="text-sm text-muted-foreground underline underline-offset-4 hover:text-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
        >
          Sair da conta
        </button>
      }
    >
      <form noValidate onSubmit={form.handleSubmit} className="flex flex-col gap-4">
        {form.formError ? <FormAlert>{form.formError}</FormAlert> : null}

        <Field
          label="Nome do workspace"
          hint="Costuma ser o nome da sua operação ou do cliente."
          error={form.errors.name}
          required
        >
          {(control) => (
            <Input
              {...control}
              name="name"
              autoComplete="organization"
              placeholder="Ex.: Ofertas SP"
              value={form.values.name}
              invalid={form.errors.name !== undefined}
              onChange={(event) => form.setField('name', event.target.value)}
            />
          )}
        </Field>

        <Field
          label="Fuso horário"
          hint="Define o que conta como 'hoje' em todo número do painel. Dá para mudar depois."
          error={form.errors.timezone}
          required
        >
          {(control) => (
            <NativeSelect
              {...control}
              name="timezone"
              value={form.values.timezone}
              invalid={form.errors.timezone !== undefined}
              onChange={(event) => form.setField('timezone', event.target.value)}
            >
              {TIMEZONE_OPTIONS.map((option) => (
                <option key={option.id} value={option.id}>
                  {option.label}
                </option>
              ))}
            </NativeSelect>
          )}
        </Field>

        <Field label="Idioma" error={form.errors.locale} required>
          {(control) => (
            <NativeSelect
              {...control}
              name="locale"
              value={form.values.locale}
              invalid={form.errors.locale !== undefined}
              onChange={(event) => form.setField('locale', event.target.value)}
            >
              {LOCALE_OPTIONS.map((option) => (
                <option key={option.id} value={option.id}>
                  {option.label}
                </option>
              ))}
            </NativeSelect>
          )}
        </Field>

        <Button
          type="submit"
          variant="primary"
          block
          loading={form.pending}
          loadingLabel="Criando workspace"
        >
          Criar workspace
        </Button>
      </form>
    </AuthLayout>
  );
}
